import { useState, type FC } from "react";
import { useSearchParams } from "react-router-dom";
import TextLinkButton from "../../atoms/buttons/TextLinkButton";
import Card from "../../atoms/Card";
import type { InvoiceUploadUnitModel } from "../../../models/InvoiceUploadUnitModel";
import { Row } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { SortingArrowsIcon } from "../../atoms/icons/SortingArrowsIcon";
import { theme } from "../../../theme";
import { ACTIVE_STATUSES, DONE_STATUSES } from "../DocumentHandler";
import { HorizontalSpacer } from "../../atoms/Spacers";
import ClientDocumentList from "../../organisms/documentHandler/ClientDocumentList";
import { useClient } from "../../../graphql/hooks/useClient";
import Alert from "../../molecules/Alert";
import TabBar from "../../molecules/TabBar";
import type { SortDirection } from "../../../models/SortDirection";

interface Props {
  clientIdentifier: string;
}

const ClientInvoicesDocumentsPage: FC<Props> = ({ clientIdentifier }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = searchParams.get("tab") ?? "registered";

  const setSelectedTab = (tab: string) => {
    setSearchParams((prev) => {
      prev.set("tab", tab);
      return prev;
    });
  };

  const { client, error } = useClient(clientIdentifier);

  const [selectedDocuments, setSelectedDocuments] = useState<Array<string>>([]);
  const [sortDirection, setSortDirection] = useState<SortDirection>("DESC");

  const checkIfDocumentIsSelected = (documentId: string) => {
    if (selectedDocuments.includes(documentId)) {
      return true;
    } else {
      return false;
    }
  };
  const checkIfAllDocumentsInUploadUnitIsSelected = (
    uploadUnit: InvoiceUploadUnitModel,
  ) => {
    if (!uploadUnit) {
      return false;
    } else {
      const allDocumentIds =
        uploadUnit.invoiceDocuments?.map((doc) => doc.id) || [];
      const allSelected = allDocumentIds.every((id) =>
        selectedDocuments.includes(id),
      );
      return allSelected;
    }
  };

  const onToggleUploadUnit = (uploadUnit: InvoiceUploadUnitModel) => {
    if (!uploadUnit) {
      return;
    } else {
      const allDocumentIds =
        uploadUnit.invoiceDocuments?.map((doc) => doc.id) || [];
      const allSelected = allDocumentIds.every((id) =>
        selectedDocuments.includes(id),
      );
      if (allSelected) {
        //remove all documents from selected
        const filteredList = selectedDocuments.filter(
          (id) => !allDocumentIds.includes(id),
        );
        setSelectedDocuments(filteredList);
      } else {
        //add all documents to selected

        const newSelectedDocuments = [
          ...new Set([...selectedDocuments, ...allDocumentIds]),
        ];
        setSelectedDocuments(newSelectedDocuments);
      }
    }
  };

  const onToggleDocument = (documentId: string) => {
    if (selectedDocuments.length === 0) {
      setSelectedDocuments([...selectedDocuments, documentId]);
      return;
    }
    //check if document is already checked
    if (selectedDocuments.includes(documentId)) {
      //remove document from list
      const filteredList = selectedDocuments.filter((x) => x !== documentId);
      setSelectedDocuments(filteredList);
    } else {
      //add document to list
      setSelectedDocuments([...selectedDocuments, documentId]);
    }
  };

  const onClickChangeSorting = () => {
    if (sortDirection === "ASC") {
      setSortDirection("DESC");
    } else {
      setSortDirection("ASC");
    }
  };

  return (
    <div>
      <TabBar
        tabs={[
          { id: "new", name: t("documentHandler.newDocuments") },
          {
            id: "registered",
            name: t("documentHandler.registeredDocuments"),
          },
        ]}
        selectedTabId={selectedTab}
        onSelectTab={setSelectedTab}
      />
      <Row nogutter justify="end">
        <TextLinkButton
          title={
            sortDirection === "DESC"
              ? t("documentHandler.showOldestFirst")
              : t("documentHandler.showNewestFirst")
          }
          icon={<SortingArrowsIcon color={theme.colors.primary} />}
          onClick={onClickChangeSorting}
          color={theme.colors.primary}
        />
      </Row>
      <HorizontalSpacer spacing={1.5} />
      <Card>
        {error && (
          <Alert
            type="error"
            title={t("errors.couldNotFetchClient")}
            message={error?.message}
          />
        )}
        <ClientDocumentList
          clientIdentifier={clientIdentifier}
          onClearSelection={() => setSelectedDocuments([])}
          statuses={selectedTab === "new" ? ACTIVE_STATUSES : DONE_STATUSES}
          onToggleDocument={onToggleDocument}
          onToggleUploadUnit={onToggleUploadUnit}
          checkIfAllDocumentsInUploadUnitIsSelected={
            checkIfAllDocumentsInUploadUnitIsSelected
          }
          checkIfDocumentIsSelected={checkIfDocumentIsSelected}
          selectedDocuments={selectedDocuments}
          selectedClientId={clientIdentifier}
          client={client}
          sortDirection={sortDirection}
        />
      </Card>
    </div>
  );
};

export default ClientInvoicesDocumentsPage;
